/**
 * Actions de jeu et liaisons clavier.
 *
 * Les liaisons utilisent `KeyboardEvent.code` (position physique de la touche)
 * et non `key` (caractère produit). C'est volontaire : `KeyW` désigne la touche
 * en haut à gauche du bloc de déplacement quelle que soit la disposition, donc
 * ZQSD sur AZERTY et WASD sur QWERTY sans configuration supplémentaire.
 */

export const GAME_ACTIONS = [
  // Marche : le personnage avance et recule le long de son propre cap.
  "moveForward",
  "moveBackward",
  // Lacet : il pivote sur lui-même, quel que soit son sens de marche.
  "turnLeft",
  "turnRight",
  // Course : même direction, vitesse doublée.
  "run",
  // Récolte : un coup de hache, face à un arbre.
  "chop",
  "toggleDebug",
] as const;

export type GameAction = (typeof GAME_ACTIONS)[number];

/**
 * Table `code` → action. Plusieurs touches peuvent viser la même action.
 *
 * Le clavier décalque le manche unique : les deux touches latérales font
 * tourner et non translater, de sorte qu'il n'y a qu'un seul schéma de conduite
 * à apprendre, au clavier comme au pouce.
 *
 * La course est sur les deux `Shift` et la coupe sur `Espace` comme sur `E` :
 * ces deux commandes s'ajoutent à la marche sans jamais lui disputer une touche,
 * pouce gauche sur `Shift`, index droit sur `E`.
 */
export const DEFAULT_KEY_BINDINGS: Readonly<Record<string, GameAction>> = {
  KeyW: "moveForward",
  ArrowUp: "moveForward",
  KeyS: "moveBackward",
  ArrowDown: "moveBackward",
  KeyA: "turnLeft",
  ArrowLeft: "turnLeft",
  KeyD: "turnRight",
  ArrowRight: "turnRight",
  ShiftLeft: "run",
  ShiftRight: "run",
  Space: "chop",
  KeyE: "chop",
  Backquote: "toggleDebug",
};

/**
 * Touches dont on neutralise le comportement par défaut du navigateur
 * (défilement de la page sur les flèches et la barre d'espace).
 */
export const PREVENT_DEFAULT_CODES: ReadonlySet<string> = new Set([
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Space",
]);
